import { createFileRoute, type SearchSchemaInput } from "@tanstack/react-router";
import { LiveNetworkExperiment } from "@charphq/ui/components/experiments";
// Deep import rather than the `lib/experiments` barrel: `validateSearch` runs
// before the code-split component loads, so anything it reaches lands in the
// entry chunk. The barrel would drag the dataset modules in with it.
import {
  parseLiveNetworkSearch,
  type LiveNetworkSearch,
} from "@charphq/ui/lib/experiments/search";

/**
 * Code-split automatically: `vite.config.ts` runs
 * `tanstackRouter({ autoCodeSplitting: true })`, so this component leaves the
 * main bundle without needing `createLazyFileRoute`.
 *
 * `SearchSchemaInput` marks the *input* as partial while the output stays
 * complete, so a link into this route does not have to spell out every param.
 *
 * Unlike the other five experiments this route does not use
 * `ExperimentRoutePage`: Live Network has no filter bar, its own header
 * actions, and three views behind `?view=`, so it renders its own shell.
 */
export const Route = createFileRoute("/experiments/live-network")({
  validateSearch: (search: Record<string, unknown> & SearchSchemaInput) =>
    parseLiveNetworkSearch(search),
  component: RouteComponent,
});

function RouteComponent() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  /**
   * Every control writes to the URL rather than to local state, which is what
   * makes a projected screen reproducible from its address. `replace: true`
   * keeps a session of fiddling with views from burying the previous page under
   * fifty history entries.
   *
   * Re-parsing rather than spreading guarantees a complete, valid object even
   * if the previous URL was hand-edited, and drops the keys a patch sets to
   * `undefined` — which is how `?tour=1` disappears rather than becoming
   * `?tour=`.
   */
  const onSearchChange = (patch: Partial<LiveNetworkSearch>) =>
    void navigate({
      // Spread into a fresh literal: the router's reducer wants a type with an
      // implicit index signature, which an interface does not have.
      search: (previous: Record<string, unknown>) => ({
        ...parseLiveNetworkSearch({ ...previous, ...patch }),
      }),
      replace: true,
    });

  return <LiveNetworkExperiment search={search} onSearchChange={onSearchChange} />;
}
